import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react'
import {
  collection,
  collectionGroup,
  deleteField,
  doc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore'
import {
  onDisconnect as rtdbOnDisconnect,
  onValue,
  ref as rtdbRef,
  remove as rtdbRemove,
  serverTimestamp as rtdbServerTimestamp,
  set as rtdbSet,
} from 'firebase/database'
import { firebaseRtdb, firestoreDb } from '../services/firebase'
import { limitsLiftedForWorkspace } from '../utils/demoUnlock'
import {
  subscribeChatQuota,
  chatQuotaWrite,
  historyCutoff,
  resolveFallbackChatHistoryDays,
  resolveFallbackChatLimit,
  dayKeyFromOffset,
} from '../services/chatQuotaService'
import { uploadWorkspaceArtifact } from '../services/storageHubService'
import { fireAutomationEvent } from '../services/automationBus'
import { subscribeWorkspaceUserDirectory } from '../services/workspaceUserDirectory'
import { useAuth } from './useAuth'
import { useNls } from '../nls/nlsProvider'
import { useDisplayName } from '../hooks/useDisplayName'
import { isHelperOnlyAccount } from '../utils/helperMembership'
import { useWorkspace } from './useWorkspace'
import { useNavigation } from './useNavigation'
import { useTimeline } from './useTimeline'
import type { WorkspaceRole } from './WorkspaceContext'
import { roleCanPerform } from '../utils/workspacePermissions'
import { isWorkspaceOwner } from '../utils/workspaceOwners'
import { subscribeToMyEffectiveControls } from '../services/memberControlsService'
import type { MemberControls, ChatControl } from '../types/memberControls'
import type { TimelineBoardKey } from '../types/schema'
import type { AnalyticsCardMarker, Channel, ChannelRole, ChatAttachment, ChatAuthorRole, ChatCommandAction, ChatCommandStatus, ChatCommandType, ChatMessageMetadata, ChatViewerPresence, HuddleMetadata, HuddlePresence, Message, OptimisticStatus, TypingPresence, WorkspaceQuota } from '../types/chat'
import type { ArtifactMarker, EntityReference, EntityReferenceType } from '../types/entity'
import { getContextPrompt } from '../utils/chatContextPrompt'
import {
  ChatContext,
  channelScopeNodeIdField,
  type ChatContextValue,
  type ChatMember,
  type ChatMeetingContext,
  type CreateChannelDraft,
  type CreateChannelOptions,
  type SendMessageOptions,
  type UpdateChannelMembersOptions,
} from './chatContextCore'
import { isNoSignInPreviewActive } from '../debug'
import { getDemoWorkspaceData } from '../explore_seeds/ariaAndCoDemoData'
import { isWorkspaceSubscriptionRestricted } from '../utils/subscriptionStatus'
import { resolveProfileImageUrl } from '../utils/profileImage'
import { shouldBootstrapGeneralChannel } from '../utils/chatBootstrap'
import { chatChannelPermissionKey, getChatChannel, purgeArchivedChannel, subscribeChatChannels } from '../services/chatChannelService'
import { visibilityAwareInterval } from '../utils/visibilityAwareInterval'
import { getWrapperRole } from '../utils/wrapperRole'

const DEFAULT_CHAT_CHANNEL_ID = 'workspace-chat'
const DEFAULT_CHANNEL_SLUG = 'general'
const DEFAULT_CHANNEL_NAME = 'general'
const MAX_CHAT_ATTACHMENTS = 5
const HUDDLE_PRESENCE_STALE_MS = 90_000
const VIEWER_PRESENCE_STALE_MS = 90_000
const TYPING_PRESENCE_STALE_MS = 3_000
const TYPING_DEBOUNCE_MS = 1_500
const MARK_READ_DEBOUNCE_MS = 600
const SLUG_REGEX = /^[a-z0-9-]{1,32}$/
const TAG_EXTRACT_REGEX = /#([a-z0-9-]{1,32})/gi

const isChatPath = () =>
  typeof window !== 'undefined' && window.location.pathname.endsWith('/chat')

const slugify = (input: string): string =>
  input
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32)

const extractTags = (text: string): string[] => {
  const out = new Set<string>()
  for (const match of text.matchAll(TAG_EXTRACT_REGEX)) {
    const slug = match[1].toLowerCase()
    if (SLUG_REGEX.test(slug)) out.add(slug)
  }
  return Array.from(out)
}

/** Coerce a workspace membership role into the chat author-role taxonomy. */
const toAuthorRole = (role: WorkspaceRole | string | undefined): ChatAuthorRole =>
  role === 'OWNER' || role === 'ADMIN' ? 'ADMIN' : role === 'VIEWER' ? 'VIEWER' : role === 'HELPER' ? 'HELPER' : role === 'BURNER' ? 'BURNER' : 'MEMBER'


/** Safely project a channel document into a `Channel` record. */
const VALID_WHATSAPP_DIRECTIONS = new Set(['IN', 'OUT'])
const VALID_WHATSAPP_VIAS = new Set(['APP', 'PHONE', 'BOT'])
const VALID_WHATSAPP_STATUSES = new Set([
  'SENDING',
  'HELD_OUTSIDE_WINDOW',
  'PAUSED',
  'SENT',
  'DELIVERED',
  'READ',
  'FAILED',
])

// eslint-disable-next-line react-refresh/only-export-components
export const normalizeWhatsappMetadata = (value: unknown): ChatMessageMetadata['whatsapp'] | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
  const raw = value as Record<string, unknown>
  const result: NonNullable<ChatMessageMetadata['whatsapp']> = {}

  if (typeof raw.waMessageId === 'string' && raw.waMessageId.trim()) {
    result.waMessageId = raw.waMessageId.trim()
  }
  if (typeof raw.direction === 'string' && VALID_WHATSAPP_DIRECTIONS.has(raw.direction)) {
    result.direction = raw.direction as NonNullable<ChatMessageMetadata['whatsapp']>['direction']
  }
  if (typeof raw.via === 'string' && VALID_WHATSAPP_VIAS.has(raw.via)) {
    result.via = raw.via as NonNullable<ChatMessageMetadata['whatsapp']>['via']
  }
  if (typeof raw.status === 'string' && VALID_WHATSAPP_STATUSES.has(raw.status)) {
    result.status = raw.status as NonNullable<ChatMessageMetadata['whatsapp']>['status']
  }
  if (typeof raw.error === 'string' && raw.error.trim()) {
    result.error = raw.error.trim()
  }
  if (typeof raw.mediaError === 'string' && raw.mediaError.trim()) {
    result.mediaError = raw.mediaError.trim()
  }
  if (typeof raw.failureCode === 'number' || (typeof raw.failureCode === 'string' && raw.failureCode.trim())) {
    result.failureCode = raw.failureCode
  }
  if (typeof raw.failureTitle === 'string' && raw.failureTitle.trim()) {
    result.failureTitle = raw.failureTitle.trim()
  }
  if (typeof raw.imported === 'boolean') {
    result.imported = raw.imported
  }

  return Object.keys(result).length > 0 ? result : undefined
}

// eslint-disable-next-line react-refresh/only-export-components
export const normalizeChannel = (id: string, data: Record<string, unknown>): Channel => {
  const rawRoles = (data.roles && typeof data.roles === 'object') ? data.roles as Record<string, unknown> : {}
  const roles: Record<string, ChannelRole> = {}
  for (const [uid, role] of Object.entries(rawRoles)) {
    if (role === 'admin' || role === 'member') roles[uid] = role
  }
  const rawReads = (data.lastReadAt && typeof data.lastReadAt === 'object') ? data.lastReadAt as Record<string, unknown> : {}
  const lastReadAt: Record<string, unknown> = {}
  for (const [uid, ts] of Object.entries(rawReads)) {
    if (ts) lastReadAt[uid] = ts
  }
  return {
    id,
    name: typeof data.name === 'string' ? data.name : 'untitled',
    slug: typeof data.slug === 'string' ? data.slug : id,
    type: data.type === 'private' ? 'private' : 'public',
    roles,
    lastActivity: data.lastActivity ?? null,
    lastReadAt,
    createdBy: typeof data.createdBy === 'string' ? data.createdBy : '',
    createdAt: data.createdAt ?? null,
    archivedAt: data.archivedAt ?? null,
    archivedBy: typeof data.archivedBy === 'string' ? data.archivedBy : '',
    restoredAt: data.restoredAt ?? null,
    restoredBy: typeof data.restoredBy === 'string' ? data.restoredBy : '',
    permanentlyDeletedAt: data.permanentlyDeletedAt ?? null,
    permanentlyDeletedBy: typeof data.permanentlyDeletedBy === 'string' ? data.permanentlyDeletedBy : '',
    autoSummarizeForHelpers: data.autoSummarizeForHelpers === true,
    ...(typeof data.identityType === 'string' && data.identityType.trim() ? { identityType: data.identityType.trim() } : {}),
    ...(typeof data.whatsappE164 === 'string' && /^\+[1-9]\d{1,14}$/.test(data.whatsappE164.trim())
      ? { whatsappE164: data.whatsappE164.trim() }
      : {}),
    ...(data.whatsappPaused &&
    typeof data.whatsappPaused === 'object' &&
    !Array.isArray(data.whatsappPaused) &&
    typeof (data.whatsappPaused as { reason?: unknown }).reason === 'string' &&
    (data.whatsappPaused as { reason: string }).reason.trim()
      ? {
          whatsappPaused: {
            reason: (data.whatsappPaused as { reason: string }).reason.trim(),
            ...((data.whatsappPaused as { since?: unknown }).since ? { since: (data.whatsappPaused as { since: unknown }).since } : {}),
          },
        }
      : {}),
    ...(data.whatsappTemplatePendingAt ? { whatsappTemplatePendingAt: data.whatsappTemplatePendingAt } : {}),
    ...(data.whatsappLastInboundAt ? { whatsappLastInboundAt: data.whatsappLastInboundAt } : {}),
  }
}

/** Safely project a message document into a `Message` record. */
// eslint-disable-next-line react-refresh/only-export-components
export const normalizeMessage = (id: string, data: Record<string, unknown>, optimisticStatus: OptimisticStatus = 'synced'): Message => ({
  id,
  authorId: typeof data.authorId === 'string' ? data.authorId : '',
  authorName: typeof data.authorName === 'string' ? data.authorName : 'Unknown',
  authorAvatarUrl: typeof data.authorAvatarUrl === 'string' ? data.authorAvatarUrl : '',
  authorRole:
    data.authorRole === 'ADMIN' || data.authorRole === 'VIEWER' || data.authorRole === 'HELPER' || data.authorRole === 'BURNER' ? data.authorRole : 'MEMBER',
  textContent: typeof data.textContent === 'string' ? data.textContent : '',
  originalTextContent: typeof data.originalTextContent === 'string' ? data.originalTextContent : undefined,
  isDeleted: data.isDeleted === true,
  editedAt: data.editedAt ?? null,
  editedBy: typeof data.editedBy === 'string' ? data.editedBy : '',
  deletedAt: data.deletedAt ?? null,
  deletedBy: typeof data.deletedBy === 'string' ? data.deletedBy : '',
  restoredAt: data.restoredAt ?? null,
  restoredBy: typeof data.restoredBy === 'string' ? data.restoredBy : '',
  ...(Array.isArray(data.attachments)
    ? {
        attachments: data.attachments
          .filter((value): value is Record<string, unknown> => Boolean(value) && typeof value === 'object')
          .map((attachment): ChatAttachment => ({
            id: typeof attachment.id === 'string' ? attachment.id : '',
            name: typeof attachment.name === 'string' ? attachment.name : 'attachment',
            contentType: typeof attachment.contentType === 'string' ? attachment.contentType : 'application/octet-stream',
            size: typeof attachment.size === 'number' ? attachment.size : 0,
            downloadUrl: typeof attachment.downloadUrl === 'string' ? attachment.downloadUrl : '',
            storagePath: typeof attachment.storagePath === 'string' ? attachment.storagePath : '',
            ...(attachment.isUnavailable === true ? { isUnavailable: true } : {}),
            ...(typeof attachment.unavailableReason === 'string' ? { unavailableReason: attachment.unavailableReason } : {}),
          })),
      }
    : {}),
  timestamp: data.timestamp ?? null,
  ...(typeof data.replyToId === 'string' ? { replyToId: data.replyToId } : {}),
  ...(Array.isArray(data.targetTaskIds)
    ? { targetTaskIds: data.targetTaskIds.filter((value): value is string => typeof value === 'string') }
    : {}),
  ...(Array.isArray(data.mentionedElementIds)
    ? { mentionedElementIds: data.mentionedElementIds.filter((value): value is string => typeof value === 'string') }
    : {}),
  ...(Array.isArray(data.mentionedUserIds)
    ? { mentionedUserIds: data.mentionedUserIds.filter((value): value is string => typeof value === 'string') }
    : {}),
  ...(Array.isArray(data.entityReferences)
    ? { entityReferences: data.entityReferences.flatMap(normalizeEntityReference) }
    : {}),
  ...(Array.isArray(data.tags)
    ? { tags: data.tags.filter((value): value is string => typeof value === 'string' && SLUG_REGEX.test(value)) }
    : {}),
  ...(typeof data.source === 'string' && data.source.trim() === 'WHATSAPP' ? { source: 'WHATSAPP' } : {}),
  ...normalizeCommandMetadata(data.metadata),
  optimisticStatus,
})

const normalizeCommandMetadata = (value: unknown): Pick<Message, 'metadata'> => {
  if (!value || typeof value !== 'object') return {}
  const commands = Array.isArray((value as { commands?: unknown }).commands)
    ? (value as { commands: unknown[] }).commands.flatMap((candidate): ChatCommandAction[] => {
        if (!candidate || typeof candidate !== 'object') return []
        const source = candidate as Record<string, unknown>
        const type = source.type as ChatCommandType
        const status = source.status as ChatCommandStatus
        if (!['TIMELINE_MOVE', 'CHECKLIST_STATUS', 'TASK_CREATE', 'DECISION_LOG', 'BLOCKER_LOG', 'FOLLOW_UP_LOG'].includes(type)) return []
        if (!['PENDING', 'EXECUTED', 'REJECTED', 'FAILED'].includes(status)) return []
        if (typeof source.commandId !== 'string' || typeof source.signedPayload !== 'string' || !source.payload || typeof source.payload !== 'object') return []
        return [{
          commandId: source.commandId,
          actionIndex: typeof source.actionIndex === 'number' ? source.actionIndex : 0,
          type,
          payload: source.payload as ChatCommandAction['payload'],
          summary: typeof source.summary === 'string' ? source.summary : 'Workspace command',
          status,
          signedPayload: source.signedPayload,
        }]
      })
    : []
  const markers = Array.isArray((value as { markers?: unknown }).markers)
    ? (value as { markers: unknown[] }).markers.flatMap(normalizeArtifactMarker)
    : []
  const analyticsCard = normalizeAnalyticsCardMarker((value as { analyticsCard?: unknown }).analyticsCard)
  const chartCard = normalizeChartCardMarker((value as { chartCard?: unknown }).chartCard)
  const whatsapp = normalizeWhatsappMetadata((value as { whatsapp?: unknown }).whatsapp)
  const fieldPassSummary = normalizeFieldPassSummaryMarker((value as { fieldPassSummary?: unknown }).fieldPassSummary)
  return commands.length > 0 || markers.length > 0 || analyticsCard || chartCard || whatsapp || fieldPassSummary
    ? {
        metadata: {
          ...(commands.length > 0 ? { commands } : {}),
          ...(markers.length > 0 ? { markers } : {}),
          ...(analyticsCard ? { analyticsCard } : {}),
          ...(chartCard ? { chartCard } : {}),
          ...(whatsapp ? { whatsapp } : {}),
          ...(fieldPassSummary ? { fieldPassSummary } : {}),
        },
      }
    : {}
}

const normalizeFieldPassSummaryMarker = (value: unknown): ChatMessageMetadata['fieldPassSummary'] | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
  const source = value as Record<string, unknown>
  if (typeof source.passId !== 'string') return undefined
  return {
    passId: source.passId,
    displayName: typeof source.displayName === 'string' ? source.displayName : null,
    phone: typeof source.phone === 'string' ? source.phone : null,
    doneCount: typeof source.doneCount === 'number' ? source.doneCount : 0,
    proofCount: typeof source.proofCount === 'number' ? source.proofCount : 0,
    firstAt: typeof source.firstAt === 'string' ? source.firstAt : null,
    lastAt: typeof source.lastAt === 'string' ? source.lastAt : null,
    cleanedUpAt: typeof source.cleanedUpAt === 'string' ? source.cleanedUpAt : null,
    workspaceId: typeof source.workspaceId === 'string' ? source.workspaceId : '',
  }
}

const normalizeChartCardMarker = (value: unknown): ChatMessageMetadata['chartCard'] | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
  const source = value as Record<string, unknown>
  if (
    typeof source.chartId !== 'string'
    || typeof source.title !== 'string'
    || typeof source.snapshotPngUrl !== 'string'
    || typeof source.sharedAt !== 'string'
  ) return undefined
  return {
    chartId: source.chartId,
    title: source.title,
    snapshotPngUrl: source.snapshotPngUrl,
    sharedAt: source.sharedAt,
  }
}

const normalizeAnalyticsCardMarker = (value: unknown): AnalyticsCardMarker | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
  const source = value as Record<string, unknown>
  if (
    typeof source.moduleId !== 'string'
    || typeof source.moduleTitle !== 'string'
    || typeof source.sparklinePngDataUrl !== 'string'
    || typeof source.generatedAt !== 'string'
    || typeof source.deepLinkPath !== 'string'
    || !Array.isArray(source.topRecords)
  ) return undefined

  const topRecords = source.topRecords.flatMap((candidate): AnalyticsCardMarker['topRecords'] => {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return []
    const record = candidate as Record<string, unknown>
    if (typeof record.id !== 'string' || typeof record.primary !== 'string') return []
    return [{
      id: record.id,
      primary: record.primary,
      ...(typeof record.secondary === 'string' ? { secondary: record.secondary } : {}),
      ...(typeof record.numeric === 'number' && Number.isFinite(record.numeric) ? { numeric: record.numeric } : {}),
    }]
  })

  return {
    moduleId: source.moduleId,
    moduleTitle: source.moduleTitle,
    sparklinePngDataUrl: source.sparklinePngDataUrl,
    topRecords,
    generatedAt: source.generatedAt,
    deepLinkPath: source.deepLinkPath,
  }
}

const entityTypes = new Set<EntityReferenceType>(['ELEMENT', 'CHECKLIST', 'MEETING', 'FILE', 'USER'])
const markerTypes = new Set<ArtifactMarker['type']>(['DECISION', 'BLOCKER', 'FOLLOW_UP', 'COMMAND'])

const normalizeEntityReference = (candidate: unknown): EntityReference[] => {
  if (!candidate || typeof candidate !== 'object') return []
  const source = candidate as Record<string, unknown>
  if (!entityTypes.has(source.entityType as EntityReferenceType) || typeof source.entityId !== 'string' || !source.entityId) return []
  const snapshot = source.snapshot && typeof source.snapshot === 'object' && !Array.isArray(source.snapshot)
    ? source.snapshot as EntityReference['snapshot']
    : {}
  return [{ entityType: source.entityType as EntityReferenceType, entityId: source.entityId, snapshot }]
}

const normalizeArtifactMarker = (candidate: unknown): ArtifactMarker[] => {
  if (!candidate || typeof candidate !== 'object') return []
  const source = candidate as Record<string, unknown>
  if (!markerTypes.has(source.type as ArtifactMarker['type'])) return []
  return [{
    type: source.type as ArtifactMarker['type'],
    text: typeof source.text === 'string' ? source.text.slice(0, 1000) : '',
    entityReferences: Array.isArray(source.entityReferences) ? source.entityReferences.flatMap(normalizeEntityReference) : [],
    createdBy: typeof source.createdBy === 'string' ? source.createdBy : undefined,
    createdAt: source.createdAt,
  }]
}

const normalizeHuddlePresence = (id: string, data: Record<string, unknown>): HuddlePresence => ({
  uid: typeof data.uid === 'string' ? data.uid : id,
  displayName: typeof data.displayName === 'string' ? data.displayName : 'Member',
  photoURL: typeof data.photoURL === 'string' ? data.photoURL : '',
  isListening: data.isListening === true,
  isSpeaking: data.isSpeaking === true,
  updatedAt: typeof data.updatedAt === 'number' ? data.updatedAt : null,
})

const normalizeTypingPresence = (id: string, data: Record<string, unknown>): TypingPresence => ({
  uid: typeof data.uid === 'string' ? data.uid : id,
  displayName: typeof data.displayName === 'string' ? data.displayName : 'Member',
  updatedAt: typeof data.updatedAt === 'number' ? data.updatedAt : null,
})

const normalizeViewerPresence = (id: string, data: Record<string, unknown>): ChatViewerPresence => ({
  uid: typeof data.uid === 'string' ? data.uid : id,
  displayName: typeof data.displayName === 'string' ? data.displayName : 'Member',
  photoURL: typeof data.photoURL === 'string' ? data.photoURL : '',
  updatedAt: typeof data.updatedAt === 'number' ? data.updatedAt : null,
})

const normalizeHuddleMetadata = (id: string, data: Record<string, unknown>): HuddleMetadata => ({
  channelId: id,
  hostUid: typeof data.hostUid === 'string' ? data.hostUid : '',
  startedAt: data.startedAt ?? null,
  participants: Array.isArray(data.participants)
    ? (data.participants as unknown[])
        .filter((p): p is Record<string, unknown> => Boolean(p) && typeof p === 'object')
        .map((p) => ({
          uid: typeof p.uid === 'string' ? p.uid : '',
          avatarUrl: typeof p.avatarUrl === 'string' ? p.avatarUrl : '',
          displayName: typeof p.displayName === 'string' ? p.displayName : 'Member',
        }))
        .filter((p) => p.uid)
    : [],
})

// Firebase RTDB emits `permission_denied` for both truly-non-member sessions
// and the transient window after a workspace is created but before the
// `workspaceMembers/{ws}/{uid}` mirror trigger has run. Neither case is a
// bug worth `console.warn` — mirror the Firestore listeners' pattern and
// downgrade authorization signals to info while preserving the loud warning
// for genuine transport/format failures.
const logRtdbListenerError = (label: string, workspaceId: string, channelId: string, error: unknown): void => {
  const message = (error as { message?: unknown })?.message
  if (typeof message === 'string' && message.toLowerCase().includes('permission_denied')) {
    console.info(`[ChatContext] ${label} presence unavailable for this session.`, workspaceId, channelId)
    return
  }
  console.warn('[RTDB listener failed]', label, workspaceId, channelId, error)
}

const timestampMillis = (value: unknown): number => {
  if (typeof value === 'number') return value
  if (value && typeof value === 'object' && 'toMillis' in value) {
    try {
      return (value as { toMillis: () => number }).toMillis()
    } catch {
      return 0
    }
  }
  return 0
}

const normalizeMentionToken = (value: string) => value.replace(/\s+/g, '').toLowerCase()

const resolveMentionedUserIds = (text: string, members: ChatMember[], authorId: string) => {
  const mentionTokens = new Set(
    Array.from(text.matchAll(/@([A-Za-z0-9._-]+)/g)).map((match) => match[1].toLowerCase()),
  )
  if (mentionTokens.size === 0) return []

  return Array.from(new Set(
    members
      .filter((member) => member.uid && member.uid !== authorId)
      .filter((member) => {
        const candidates = [
          normalizeMentionToken(member.name),
          normalizeMentionToken(member.email.split('@')[0] ?? ''),
          member.uid.toLowerCase(),
        ]
        return candidates.some((candidate) => mentionTokens.has(candidate))
      })
      .map((member) => member.uid),
  ))
}

const EMPTY_CHANNELS: Channel[] = []
const EMPTY_MEMBERS: ChatMember[] = []
const EMPTY_HUDDLE_PRESENCE: HuddlePresence[] = []
const EMPTY_VIEWER_PRESENCE: ChatViewerPresence[] = []
const EMPTY_TYPING_PRESENCE: TypingPresence[] = []
const EMPTY_MESSAGES_MAP = new Map<string, Message[]>()
const EMPTY_READY_SET = new Set<string>()

export const ChatProvider = ({ children }: PropsWithChildren) => {
  const { t } = useNls()
  const { currentUser, userProfile } = useAuth()
  const currentDisplayName = useDisplayName(userProfile ?? currentUser)
  const currentProfileImageUrl = resolveProfileImageUrl(userProfile, currentUser)
  const { activeWorkspaceId, activeRole, canPerform, workspaces, appMode } = useWorkspace()
  const activeWorkspace = workspaces.find((workspace) => workspace.id === activeWorkspaceId)
  // Chat is only available on TEAM workspaces with the chat.view capability
  // (matches the App-level `isChatAvailable` gate). Skipping chat listeners
  // on personal workspaces removes 2 always-on channel subscriptions each.
  const chatAvailable = appMode === 'TEAM' && canPerform('chat.view')
  const { activeGoalId, activeTaskId, activeSubTaskId } = useNavigation()
  const { elements, isAccessRestricted } = useTimeline()

  const [effectiveControls, setEffectiveControls] = useState<MemberControls | null>(null)

  useEffect(() => {
    if (!activeWorkspaceId) {
      queueMicrotask(() => setEffectiveControls(null))
      return
    }
    return subscribeToMyEffectiveControls(activeWorkspaceId, (controls) => {
      setEffectiveControls(controls)
    })
  }, [activeWorkspaceId])

  const isOwner = Boolean(
    activeRole === 'OWNER' ||
    (activeWorkspace && currentUser?.uid && isWorkspaceOwner(activeWorkspace, currentUser.uid)),
  )
  const chatMode: ChatControl = isOwner ? 'FULL' : (effectiveControls?.chat ?? 'FULL')
  const isChatOff = chatMode === 'OFF'

  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null)
  const [prevWorkspaceId, setPrevWorkspaceId] = useState(activeWorkspaceId)
  if (prevWorkspaceId !== activeWorkspaceId) {
    setPrevWorkspaceId(activeWorkspaceId)
    setSelectedChannelId(null)
  }
  const activeChannelId = !activeWorkspaceId ? null : (selectedChannelId ?? DEFAULT_CHAT_CHANNEL_ID)
  const setActiveChannelId = useCallback((channelId: string | null) => {
    setSelectedChannelId(channelId)
  }, [])

  const demoData = activeWorkspaceId ? getDemoWorkspaceData(activeWorkspaceId) : null
  const isPreview = isNoSignInPreviewActive()

  const [subscribedChannelsState, setSubscribedChannelsState] = useState<{ workspaceId: string; channels: Channel[]; ready: boolean } | null>(null)
  const channelsSnapshotReady = useMemo(() => {
    if (!activeWorkspaceId || !currentUser || isChatOff || !chatAvailable || isPreview) return false
    if (demoData) return true
    if (subscribedChannelsState?.workspaceId === activeWorkspaceId) {
      return subscribedChannelsState.ready
    }
    return false
  }, [activeWorkspaceId, currentUser, isChatOff, chatAvailable, isPreview, demoData, subscribedChannelsState])

  const channelsList = useMemo(() => {
    if (!activeWorkspaceId || !currentUser || isChatOff || !chatAvailable || isPreview) return EMPTY_CHANNELS
    if (demoData) return demoData.channels
    if (subscribedChannelsState?.workspaceId === activeWorkspaceId) {
      return subscribedChannelsState.channels
    }
    return EMPTY_CHANNELS
  }, [activeWorkspaceId, currentUser, isChatOff, chatAvailable, isPreview, demoData, subscribedChannelsState])

  // Per-channel cache. Populated by persistent listeners we open on first
  // visit; kept alive so channel switches show cached messages instantly
  // (no clear-and-refetch flicker). Cleared on workspace change / unmount.
  const [messagesByChannelId, setMessagesByChannelId] = useState<Map<string, Message[]>>(EMPTY_MESSAGES_MAP)
  // Channels whose first snapshot has arrived. Drives the `isLoading` flag.
  const [readyChannelIds, setReadyChannelIds] = useState<Set<string>>(EMPTY_READY_SET)
  const [prevMsgWorkspaceId, setPrevMsgWorkspaceId] = useState(activeWorkspaceId)
  if (prevMsgWorkspaceId !== activeWorkspaceId) {
    setPrevMsgWorkspaceId(activeWorkspaceId)
    if (!activeWorkspaceId || isPreview) {
      setMessagesByChannelId(EMPTY_MESSAGES_MAP)
      setReadyChannelIds(EMPTY_READY_SET)
    } else if (demoData) {
      const cache = new Map<string, Message[]>()
      const ready = new Set<string>()
      for (const [cid, msgs] of demoData.messagesByChannelId) {
        cache.set(cid, [...msgs])
        ready.add(cid)
      }
      setMessagesByChannelId(cache)
      setReadyChannelIds(ready)
    } else {
      setMessagesByChannelId(EMPTY_MESSAGES_MAP)
      setReadyChannelIds(EMPTY_READY_SET)
    }
  }

  const [activeTagFilter, setActiveTagFilter] = useState<string | null>(null)
  const demoTagFilteredMessages = useMemo(() => {
    if (!demoData || activeChannelId || !activeTagFilter) return null
    const all: Message[] = []
    for (const [, chanMsgs] of demoData.messagesByChannelId) {
      for (const msg of chanMsgs) {
        if ((msg.tags ?? []).includes(activeTagFilter)) all.push(msg)
      }
    }
    return all
  }, [demoData, activeChannelId, activeTagFilter])

  const [subscribedMessagesStreamOverride, setSubscribedMessagesStreamOverride] = useState<Message[] | null>(null)
  const messagesStreamOverride = useMemo<Message[] | null>(() => {
    if (!activeWorkspaceId || activeChannelId || !activeTagFilter || isPreview) return null
    if (demoData) return demoTagFilteredMessages
    return subscribedMessagesStreamOverride
  }, [activeWorkspaceId, activeChannelId, activeTagFilter, isPreview, demoData, demoTagFilteredMessages, subscribedMessagesStreamOverride])

  const [subscribedWorkspaceQuota, setSubscribedWorkspaceQuota] = useState<{ workspaceId: string; quota: WorkspaceQuota } | null>(null)
  const workspaceQuota = useMemo<WorkspaceQuota | null>(() => {
    if (!activeWorkspaceId || isChatOff || isPreview) return null
    if (demoData) return demoData.workspaceQuota
    if (subscribedWorkspaceQuota?.workspaceId === activeWorkspaceId) {
      return subscribedWorkspaceQuota.quota
    }
    return null
  }, [activeWorkspaceId, isChatOff, isPreview, demoData, subscribedWorkspaceQuota])

  const historyDays = workspaceQuota?.historyDays ?? -1
  const hasWorkspaceQuota = Boolean(workspaceQuota)
  const [historySlideTick, setHistorySlideTick] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => {
      setHistorySlideTick((prev) => prev + 1)
    }, 60 * 60 * 1000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    channelMessageSubsRef.current.forEach((unsub) => unsub())
    channelMessageSubsRef.current.clear()
  }, [historyDays, historySlideTick])

  const isHelper = Boolean(
    isHelperOnlyAccount({ userProfile, currentUser, workspaces, activeRole }) ||
    activeRole === 'HELPER' ||
    getWrapperRole() === 'helper'
  )
  const [subscribedMembers, setSubscribedMembers] = useState<ChatMember[]>(EMPTY_MEMBERS)
  const members = useMemo(() => {
    if (!activeWorkspaceId || isHelper || isPreview) return EMPTY_MEMBERS
    return subscribedMembers
  }, [activeWorkspaceId, isHelper, isPreview, subscribedMembers])

  const [subscribedHuddlePresence, setSubscribedHuddlePresence] = useState<HuddlePresence[]>(EMPTY_HUDDLE_PRESENCE)
  const huddlePresence = useMemo(() => {
    if (!activeWorkspaceId || !activeChannelId || isPreview) return EMPTY_HUDDLE_PRESENCE
    return subscribedHuddlePresence
  }, [activeWorkspaceId, activeChannelId, isPreview, subscribedHuddlePresence])

  const [subscribedViewerPresence, setSubscribedViewerPresence] = useState<ChatViewerPresence[]>(EMPTY_VIEWER_PRESENCE)
  const [isChatRouteActive, setIsChatRouteActive] = useState(isChatPath)
  const viewerPresence = useMemo(() => {
    if (!isChatRouteActive || !activeWorkspaceId || !activeChannelId || isPreview) return EMPTY_VIEWER_PRESENCE
    return subscribedViewerPresence
  }, [isChatRouteActive, activeWorkspaceId, activeChannelId, isPreview, subscribedViewerPresence])

  const [subscribedHuddleMetadata, setSubscribedHuddleMetadata] = useState<HuddleMetadata | null>(null)
  const huddleMetadata = useMemo(() => {
    if (!activeWorkspaceId || !activeChannelId || isPreview) return null
    return subscribedHuddleMetadata
  }, [activeWorkspaceId, activeChannelId, isPreview, subscribedHuddleMetadata])

  const [subscribedTypingPresence, setSubscribedTypingPresence] = useState<TypingPresence[]>(EMPTY_TYPING_PRESENCE)
  const typingPresence = useMemo(() => {
    if (!activeWorkspaceId || !activeChannelId || isPreview) return EMPTY_TYPING_PRESENCE
    return subscribedTypingPresence
  }, [activeWorkspaceId, activeChannelId, isPreview, subscribedTypingPresence])

  const huddleSessionKey = `${activeWorkspaceId ?? ""}:${activeChannelId ?? ""}:${currentUser?.uid ?? ""}`
  const [prevHuddleSessionKey, setPrevHuddleSessionKey] = useState(huddleSessionKey)
  const [localHuddleState, setLocalHuddleState] = useState({ isListening: false, isSpeaking: false })
  if (prevHuddleSessionKey !== huddleSessionKey) {
    setPrevHuddleSessionKey(huddleSessionKey)
    setLocalHuddleState({ isListening: false, isSpeaking: false })
  }

  const [contextBoard, setContextBoard] = useState<TimelineBoardKey>('DASHBOARD')
  const [meetingContext, setMeetingContext] = useState<ChatMeetingContext>(null)
  const [pendingCreateChannelDraft, setPendingCreateChannelDraft] = useState<CreateChannelDraft | null>(null)
  const typingTimerRef = useRef<number | null>(null)
  const markReadTimerRef = useRef<number | null>(null)
  const generalBootstrappedRef = useRef<Set<string>>(new Set())
  const channelPermissionDeniedRef = useRef<Set<string>>(new Set())
  // Active per-channel message subscriptions. Kept alive across channel
  // switches; torn down only when the workspace changes or the provider unmounts.
  const channelMessageSubsRef = useRef<Map<string, () => void>>(new Map())
  // Latest channelsList mirror — used by the cross-channel tag listener to
  // resolve visible channel IDs without adding channelsList to its deps
  // (which would resubscribe on every channel-doc update, incl. every send).
  const channelsListRef = useRef<Channel[]>([])
  useEffect(() => { channelsListRef.current = channelsList }, [channelsList])

  useEffect(() => {
    const sync = () => setIsChatRouteActive(isChatPath())
    const originalPushState = window.history.pushState
    const originalReplaceState = window.history.replaceState
    window.history.pushState = function pushState(...args) {
      originalPushState.apply(this, args)
      sync()
    }
    window.history.replaceState = function replaceState(...args) {
      originalReplaceState.apply(this, args)
      sync()
    }
    window.addEventListener('popstate', sync)
    return () => {
      window.history.pushState = originalPushState
      window.history.replaceState = originalReplaceState
      window.removeEventListener('popstate', sync)
    }
  }, [])

  const contextMetadata = useMemo(() => ({
    activeGoalId,
    activeTaskId,
    activeSubTaskId,
    currentBoard: contextBoard,
  }), [activeGoalId, activeSubTaskId, activeTaskId, contextBoard])
  const contextPrompt = useMemo(() => `${getContextPrompt({
    board: contextBoard,
    goal: elements.find((element) => element.id === activeGoalId) ?? null,
    task: elements.find((element) => element.id === activeTaskId) ?? null,
    subtask: elements.find((element) => element.id === activeSubTaskId) ?? null,
  })}${meetingContext ? `\nMeeting context: ${meetingContext.title} (${meetingContext.status}, ${meetingContext.sourceProvider}) at ${meetingContext.scheduledAt}.\nAgenda:\n${meetingContext.agendaMarkdown}` : ''}`, [activeGoalId, activeSubTaskId, activeTaskId, contextBoard, elements, meetingContext])



  // ── Listener: parent workspace quota frame ───────────────────────────────
  // Whenever the active workspace changes, re-bind a real-time listener to the
  // workspace effective limits and daily chat usage documents.
  useEffect(() => {
    if (!activeWorkspaceId || isChatOff || isNoSignInPreviewActive()) {
      return
    }
    const demo = getDemoWorkspaceData(activeWorkspaceId)
    if (demo) {
      return
    }

    const wsId = activeWorkspaceId
    const unsubscribe = subscribeChatQuota(
      wsId,
      (quota) => {
        setSubscribedWorkspaceQuota({ workspaceId: wsId, quota })
      },
      {
        onError: () => {
          const plan = activeWorkspace?.plan ?? null
          setSubscribedWorkspaceQuota({
            workspaceId: wsId,
            quota: {
              limit: resolveFallbackChatLimit(plan),
              usedToday: 0,
              dayKey: dayKeyFromOffset(Date.now(), 0),
              isOverQuota: false,
              historyDays: resolveFallbackChatHistoryDays(plan),
            },
          })
        },
      },
    )

    return () => unsubscribe()
  }, [activeWorkspaceId, activeWorkspace?.plan, isChatOff])

  // ── Listener: channel directory ──────────────────────────────────────────
  // Keeps `channelsList` in sync with the workspace's channels sub-collection.
  // Visible to the current user is filtered client-side (rules enforce reads):
  // public channels for any participant + private channels containing the uid.
  useEffect(() => {
    if (!activeWorkspaceId || !currentUser || isChatOff || !chatAvailable || isNoSignInPreviewActive()) {
      return
    }
    const demoChannels = getDemoWorkspaceData(activeWorkspaceId)
    if (demoChannels) {
      return
    }
    const permissionKey = chatChannelPermissionKey(activeWorkspaceId, currentUser.uid, Boolean(isAccessRestricted))
    if (channelPermissionDeniedRef.current.has(permissionKey)) {
      return
    }

    const wsId = activeWorkspaceId
    const applyChannels = (publicChannels: Channel[], privateChannels: Channel[], ready: boolean) => {
      const byId = new Map<string, Channel>()
      publicChannels.forEach((channel) => byId.set(channel.id, channel))
      privateChannels.forEach((channel) => byId.set(channel.id, channel))
      const next = Array.from(byId.values())
      next.sort((a, b) => {
        const am = timestampMillis(a.lastActivity)
        const bm = timestampMillis(b.lastActivity)
        if (am !== bm) return bm - am
        return a.name.localeCompare(b.name)
      })
      setSubscribedChannelsState({ workspaceId: wsId, channels: next, ready })
    }

    let isCancelled = false
    const unsubscribe = subscribeChatChannels(
      activeWorkspaceId,
      { restricted: Boolean(isAccessRestricted), uid: currentUser.uid },
      {
        permissionDeniedKeys: channelPermissionDeniedRef.current,
        onUpdate: (publicDocs, privateDocs, ready) => {
          if (isCancelled) return
          applyChannels(
            publicDocs.map((doc) => normalizeChannel(doc.id, doc.data)),
            privateDocs.map((doc) => normalizeChannel(doc.id, doc.data)),
            ready,
          )
        },
        onPermissionDenied: () => {
          if (isCancelled) return
          if (isHelper) {
            void getChatChannel(wsId, DEFAULT_CHAT_CHANNEL_ID).then((channelDoc) => {
              if (isCancelled) return
              if (channelDoc) {
                setSubscribedChannelsState({
                  workspaceId: wsId,
                  channels: [normalizeChannel(channelDoc.id, channelDoc.data)],
                  ready: true,
                })
              } else {
                setSubscribedChannelsState({ workspaceId: wsId, channels: EMPTY_CHANNELS, ready: false })
              }
            })
            return
          }

          setSubscribedChannelsState({ workspaceId: wsId, channels: EMPTY_CHANNELS, ready: false })
        },
      },
    )

    return () => {
      isCancelled = true
      unsubscribe()
    }
  }, [activeWorkspaceId, currentUser, chatAvailable, isAccessRestricted, userProfile?.role, isChatOff, isHelper])

  // ── Bootstrap `#general` ─────────────────────────────────────────────────
  // On the first non-empty channels snapshot for a workspace, if the legacy
  // `workspace-chat` doc is missing the new schema fields, upgrade it in place.
  // If no channels exist, seed `workspace-chat` with the #general shape.
  useEffect(() => {
    if (!activeWorkspaceId || !currentUser) return
    if (isAccessRestricted) return
    if (isNoSignInPreviewActive()) return
    if (getDemoWorkspaceData(activeWorkspaceId)) return
    const permissionKey = chatChannelPermissionKey(activeWorkspaceId, currentUser.uid, Boolean(isAccessRestricted))
    if (channelPermissionDeniedRef.current.has(permissionKey)) return
    if (generalBootstrappedRef.current.has(activeWorkspaceId)) return
    if (!channelsSnapshotReady) return

    if (!shouldBootstrapGeneralChannel(channelsList, DEFAULT_CHAT_CHANNEL_ID, DEFAULT_CHANNEL_SLUG, isAccessRestricted)) {
      generalBootstrappedRef.current.add(activeWorkspaceId)
      return
    }

    generalBootstrappedRef.current.add(activeWorkspaceId)
    const channelRef = doc(firestoreDb, 'workspaces', activeWorkspaceId, 'channels', DEFAULT_CHAT_CHANNEL_ID)
    const slugRef = doc(firestoreDb, 'workspaces', activeWorkspaceId, 'channelSlugs', DEFAULT_CHANNEL_SLUG)
    void runTransaction(firestoreDb, async (tx) => {
      const existing = await tx.get(channelRef)
      const existingSlug = await tx.get(slugRef)
      const payload = {
        name: DEFAULT_CHANNEL_NAME,
        slug: DEFAULT_CHANNEL_SLUG,
        type: 'public' as const,
        roles: { [currentUser.uid]: 'admin' as const },
        lastActivity: serverTimestamp(),
        lastReadAt: {},
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
      }
      tx.set(channelRef, payload, { merge: true })
      if (!existingSlug.exists()) {
        tx.set(slugRef, { channelId: DEFAULT_CHAT_CHANNEL_ID, createdBy: currentUser.uid })
      }
      return existing.exists()
    }).catch((error) => {
      // Non-fatal: another client may have raced us, or rules may reject the
      // bootstrap when the caller is not yet a workspace participant.
      const code = typeof error === 'object' && error !== null && 'code' in error
        ? (error as { code?: unknown }).code
        : undefined
      if (code === 'failed-precondition' || code === 'aborted') return
      console.warn('[Firestore write failed] chat bootstrap general channel', activeWorkspaceId, error)
    })
  }, [activeWorkspaceId, channelsList, channelsSnapshotReady, currentUser, isAccessRestricted])

  // Reset the bootstrap memo when the workspace changes.
  useEffect(() => {
    if (!activeWorkspaceId) generalBootstrappedRef.current = new Set()
  }, [activeWorkspaceId])

  // ── Workspace teardown + demo hydration ──────────────────────────────────
  // When the workspace changes (or the provider unmounts) we drop every
  // per-channel message subscription and clear the cache. When the workspace
  // has demo/seed data, we hydrate the cache directly with no listeners.
  useEffect(() => {
    const subs = channelMessageSubsRef.current
    if (!activeWorkspaceId || isNoSignInPreviewActive() || demoData) {
      subs.forEach((unsub) => unsub())
      subs.clear()
      return
    }
    return () => {
      subs.forEach((unsub) => unsub())
      subs.clear()
    }
  }, [activeWorkspaceId, demoData])

  useEffect(() => {
    if (isChatOff) {
      channelMessageSubsRef.current.forEach((unsub) => unsub())
      channelMessageSubsRef.current.clear()
      queueMicrotask(() => {
        setMessagesByChannelId(new Map())
        setReadyChannelIds(new Set())
      })
    }
  }, [isChatOff])

  // ── Listener: per-channel message stream (persistent) ────────────────────
  // Opens ONE snapshot listener per channel the user visits and keeps it alive
  // for the rest of the workspace session. Switching channels no longer clears
  // or refetches anything — the derived `messagesStream` just points at a
  // different slice of the cache. Sending a message no longer resubscribes
  // (the old effect resubscribed on every `channelsList` change; this one has
  // no such dependency).
  useEffect(() => {
    if (!activeWorkspaceId || !activeChannelId || isChatOff) return
    if (isNoSignInPreviewActive()) return
    if (getDemoWorkspaceData(activeWorkspaceId)) return
    if (!workspaceQuota) return
    if (channelMessageSubsRef.current.has(activeChannelId)) return

    const cid = activeChannelId
    const wsId = activeWorkspaceId
    const cutoff = historyCutoff(Date.now(), historyDays)
    const q = cutoff
      ? query(
          collection(firestoreDb, 'workspaces', wsId, 'channels', cid, 'messages'),
          where('timestamp', '>=', cutoff),
          orderBy('timestamp', 'asc'),
        )
      : query(
          collection(firestoreDb, 'workspaces', wsId, 'channels', cid, 'messages'),
          orderBy('timestamp', 'asc'),
        )
    const markReady = () => setReadyChannelIds((prev) => {
      if (prev.has(cid)) return prev
      const next = new Set(prev)
      next.add(cid)
      return next
    })
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const next = snapshot.docs.map((messageDoc) =>
          normalizeMessage(
            messageDoc.id,
            messageDoc.data() as Record<string, unknown>,
            messageDoc.metadata.hasPendingWrites ? 'pending' : 'synced',
          ),
        )
        setMessagesByChannelId((prev) => {
          const nextMap = new Map(prev)
          nextMap.set(cid, next)
          return nextMap
        })
        markReady()
      },
      (error) => {
        console.warn('[Firestore listener failed] chat messages', cid, error)
        // Flip ready so the UI stops showing the loading state even if the
        // sub failed (permission-denied on a private channel we can't read).
        markReady()
      },
    )
    channelMessageSubsRef.current.set(cid, unsubscribe)
  }, [activeWorkspaceId, activeChannelId, historyDays, historySlideTick, hasWorkspaceQuota, isChatOff])

  // ── Listener: cross-channel tag scope ────────────────────────────────────
  // Only used when a tag filter is set AND no channel is selected. Uses
  // `channelsListRef` to read the current visible-channel set inside the
  // snapshot callback, so channel-doc updates (which fire on every message
  // send via `lastActivity`) don't resubscribe this listener.
  useEffect(() => {
    if (!activeWorkspaceId || isNoSignInPreviewActive() || demoData || activeChannelId || !activeTagFilter || !workspaceQuota) {
      return
    }

    const wsId = activeWorkspaceId
    const tag = activeTagFilter
    const cutoff = historyCutoff(Date.now(), historyDays)
    const q = cutoff
      ? query(
          collectionGroup(firestoreDb, 'messages'),
          where('tags', 'array-contains', tag),
          where('timestamp', '>=', cutoff),
          orderBy('timestamp', 'asc'),
        )
      : query(
          collectionGroup(firestoreDb, 'messages'),
          where('tags', 'array-contains', tag),
          orderBy('timestamp', 'asc'),
        )
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const visibleChannelIds = new Set(channelsListRef.current.map((c) => c.id))
        const next: Message[] = []
        for (const docSnap of snapshot.docs) {
          const path = docSnap.ref.path
          // Path shape: workspaces/{wid}/channels/{cid}/messages/{mid}
          const parts = path.split('/')
          if (parts.length !== 6) continue
          if (parts[0] !== 'workspaces' || parts[1] !== wsId) continue
          const cid = parts[3]
          if (!visibleChannelIds.has(cid)) continue
          next.push(normalizeMessage(
            docSnap.id,
            docSnap.data() as Record<string, unknown>,
            docSnap.metadata.hasPendingWrites ? 'pending' : 'synced',
          ))
        }
        setSubscribedMessagesStreamOverride(next)
      },
      (error) => {
        console.warn('[Firestore listener failed] chat cross-channel tag scope', tag, error)
        setSubscribedMessagesStreamOverride([])
      },
    )
    return () => unsubscribe()
  }, [activeWorkspaceId, activeChannelId, activeTagFilter, demoData, historyDays, historySlideTick, workspaceQuota])

  // ── Derived: active messages stream + loading ────────────────────────────
  // messagesStream is derived from the cache slice for `activeChannelId`
  // (with an optional in-memory tag filter). isLoading is true only when the
  // active channel has no cached snapshot yet — subsequent visits show the
  // last-known messages instantly with no blanking or spinner.
  const messagesStream = useMemo<Message[]>(() => {
    if (messagesStreamOverride) return messagesStreamOverride
    if (!activeChannelId) return []
    const cached = messagesByChannelId.get(activeChannelId) ?? []
    if (activeTagFilter) {
      return cached.filter((msg) => (msg.tags ?? []).includes(activeTagFilter))
    }
    return cached
  }, [messagesStreamOverride, activeChannelId, messagesByChannelId, activeTagFilter])

  const isLoading = useMemo<boolean>(() => {
    if (messagesStreamOverride !== null) return false
    if (!activeChannelId) return false
    return !readyChannelIds.has(activeChannelId)
  }, [messagesStreamOverride, activeChannelId, readyChannelIds])

  // ── Listener: passive channel viewers (RTDB) ─────────────────────────────
  useEffect(() => {
    if (!isChatRouteActive || !activeWorkspaceId || !activeChannelId || isNoSignInPreviewActive()) {
      return
    }

    const viewersRef = rtdbRef(firebaseRtdb, `viewers/${activeWorkspaceId}/${activeChannelId}`)
    const currentViewerRef = currentUser
      ? rtdbRef(firebaseRtdb, `viewers/${activeWorkspaceId}/${activeChannelId}/${currentUser.uid}`)
      : null
    const writeViewer = () => {
      if (!currentViewerRef || !currentUser) return
      void rtdbSet(currentViewerRef, {
        uid: currentUser.uid,
        displayName: currentDisplayName || currentUser.email || 'Member',
        photoURL: currentProfileImageUrl,
        updatedAt: rtdbServerTimestamp(),
      }).catch((error) => {
        console.warn('[RTDB write failed] viewer presence', activeWorkspaceId, activeChannelId, error)
      })
    }

    writeViewer()
    if (currentViewerRef) rtdbOnDisconnect(currentViewerRef).remove().catch(() => undefined)
    const stopViewerInterval = visibilityAwareInterval(writeViewer, 30_000)
    const unsubscribe = onValue(
      viewersRef,
      (snapshot) => {
        const cutoff = Date.now() - VIEWER_PRESENCE_STALE_MS
        const value = snapshot.val() as Record<string, Record<string, unknown>> | null
        const next: ChatViewerPresence[] = []
        if (value) {
          for (const [uid, row] of Object.entries(value)) {
            const presence = normalizeViewerPresence(uid, row)
            if (timestampMillis(presence.updatedAt) < cutoff) continue
            next.push(presence)
          }
        }
        next.sort((a, b) => a.displayName.localeCompare(b.displayName))
        setSubscribedViewerPresence(next)
      },
      (error) => {
        logRtdbListenerError('viewer', activeWorkspaceId, activeChannelId, error)
      },
    )
    return () => {
      stopViewerInterval()
      unsubscribe()
      if (currentViewerRef) void rtdbRemove(currentViewerRef).catch(() => undefined)
    }
  }, [activeWorkspaceId, activeChannelId, currentDisplayName, currentProfileImageUrl, currentUser, isChatRouteActive])

  // ── Listener: huddle presence (RTDB, per active channel) ─────────────────
  useEffect(() => {
    if (!activeWorkspaceId || !activeChannelId || isNoSignInPreviewActive()) {
      return
    }
    const presenceRef = rtdbRef(firebaseRtdb, `presence/${activeWorkspaceId}/${activeChannelId}`)
    const unsubscribe = onValue(
      presenceRef,
      (snapshot) => {
        const cutoff = Date.now() - HUDDLE_PRESENCE_STALE_MS
        const value = snapshot.val() as Record<string, Record<string, unknown>> | null
        const next: HuddlePresence[] = []
        if (value) {
          for (const [uid, row] of Object.entries(value)) {
            const presence = normalizeHuddlePresence(uid, row)
            if (timestampMillis(presence.updatedAt) < cutoff) continue
            if (!presence.isListening && !presence.isSpeaking) continue
            next.push(presence)
          }
        }
        next.sort((a, b) => a.displayName.localeCompare(b.displayName))
        setSubscribedHuddlePresence(next)
      },
      (error) => {
        logRtdbListenerError('huddle', activeWorkspaceId, activeChannelId, error)
      },
    )
    return () => unsubscribe()
  }, [activeWorkspaceId, activeChannelId])

  // ── Listener: typing presence (RTDB) ─────────────────────────────────────
  useEffect(() => {
    if (!activeWorkspaceId || !activeChannelId || isNoSignInPreviewActive()) {
      return
    }
    const typingRefNode = rtdbRef(firebaseRtdb, `typing/${activeWorkspaceId}/${activeChannelId}`)
    const unsubscribe = onValue(
      typingRefNode,
      (snapshot) => {
        const cutoff = Date.now() - TYPING_PRESENCE_STALE_MS
        const value = snapshot.val() as Record<string, Record<string, unknown>> | null
        const next: TypingPresence[] = []
        if (value) {
          for (const [uid, row] of Object.entries(value)) {
            if (uid === currentUser?.uid) continue
            const presence = normalizeTypingPresence(uid, row)
            if (timestampMillis(presence.updatedAt) < cutoff) continue
            next.push(presence)
          }
        }
        next.sort((a, b) => a.displayName.localeCompare(b.displayName))
        setSubscribedTypingPresence(next)
      },
      (error) => {
        logRtdbListenerError('typing', activeWorkspaceId, activeChannelId, error)
      },
    )
    return () => unsubscribe()
  }, [activeWorkspaceId, activeChannelId, currentUser?.uid])

  // ── Listener: huddle metadata (Firestore, per active channel) ────────────
  useEffect(() => {
    if (!activeWorkspaceId || !activeChannelId || isNoSignInPreviewActive()) {
      return
    }
    const unsubscribe = onSnapshot(
      doc(firestoreDb, 'workspaces', activeWorkspaceId, 'huddles', activeChannelId),
      (snapshot) => {
        if (!snapshot.exists()) {
          setSubscribedHuddleMetadata(null)
          return
        }
        setSubscribedHuddleMetadata(normalizeHuddleMetadata(activeChannelId, snapshot.data() as Record<string, unknown>))
      },
      (error) => {
        console.warn('[Firestore listener failed] huddle metadata', activeWorkspaceId, activeChannelId, error)
      },
    )
    return () => unsubscribe()
  }, [activeWorkspaceId, activeChannelId])

  // ── Listener: workspace members ──────────────────────────────────────────
  // Pulls the active member list from `workspace_members`. Pending/declined
  // invites are filtered out client-side (status field may be absent on legacy
  // rows, so an undefined status counts as active for backward compatibility).
  useEffect(() => {
    if (!activeWorkspaceId || isHelper || isNoSignInPreviewActive()) {
      return
    }

    const unsubscribe = subscribeWorkspaceUserDirectory(
      activeWorkspaceId,
      (directory) => setSubscribedMembers(directory.map((member): ChatMember => ({
        id: member.membershipId,
        uid: member.uid,
        name: member.displayName,
        email: member.email,
        role: toAuthorRole(member.role),
        avatarUrl: member.avatarUrl,
      }))),
      (error) => {
        console.warn('[Firestore listener failed] chat members', activeWorkspaceId, error)
      },
    )

    return () => unsubscribe()
  }, [activeWorkspaceId, currentUser, userProfile?.role, isHelper])

  const clearCurrentHuddlePresence = useCallback(async () => {
    if (!activeWorkspaceId || !activeChannelId || !currentUser) return
    try {
      await rtdbRemove(rtdbRef(firebaseRtdb, `presence/${activeWorkspaceId}/${activeChannelId}/${currentUser.uid}`))
    } catch (error) {
      console.warn('[RTDB write failed] clear huddle presence', activeWorkspaceId, activeChannelId, error)
    }
  }, [activeWorkspaceId, activeChannelId, currentUser])



  useEffect(() => {
    if (!localHuddleState.isListening && !localHuddleState.isSpeaking) return undefined
    if (!activeWorkspaceId || !activeChannelId || !currentUser) return undefined

    const presenceRef = rtdbRef(firebaseRtdb, `presence/${activeWorkspaceId}/${activeChannelId}/${currentUser.uid}`)
    const writePresence = () => {
      void rtdbSet(presenceRef, {
        uid: currentUser.uid,
        displayName: currentDisplayName || currentUser.email || 'Member',
        photoURL: currentProfileImageUrl,
        isListening: localHuddleState.isListening,
        isSpeaking: localHuddleState.isSpeaking,
        updatedAt: rtdbServerTimestamp(),
      }).catch((error) => {
        console.warn('[RTDB write failed] huddle presence', activeWorkspaceId, activeChannelId, error)
      })
    }

    writePresence()
    rtdbOnDisconnect(presenceRef).remove().catch(() => undefined)
    const stopPresenceInterval = visibilityAwareInterval(writePresence, 30_000)
    return () => stopPresenceInterval()
  }, [activeWorkspaceId, activeChannelId, currentDisplayName, currentProfileImageUrl, currentUser, localHuddleState])

  useEffect(() => {
    const handleUnload = () => {
      if (!activeWorkspaceId || !activeChannelId || !currentUser) return
      void rtdbRemove(rtdbRef(firebaseRtdb, `presence/${activeWorkspaceId}/${activeChannelId}/${currentUser.uid}`))
    }
    window.addEventListener('beforeunload', handleUnload)
    return () => {
      window.removeEventListener('beforeunload', handleUnload)
      void clearCurrentHuddlePresence()
    }
  }, [activeWorkspaceId, activeChannelId, clearCurrentHuddlePresence, currentUser])



  // ── Write transaction: guarded message send ──────────────────────────────
  const sendMessage = useCallback(
    async (textContent: string, options?: SendMessageOptions) => {
      if (!currentUser) throw new Error('You must be signed in to send a message.')
      if (!canPerform('chat.send')) throw new Error('Your workspace role cannot send chat messages.')
      if (!activeWorkspaceId) throw new Error('No active workspace selected.')
      const targetChannelId = options?.channelId ?? activeChannelId
      if (!targetChannelId) throw new Error('No active channel selected.')
      if (!limitsLiftedForWorkspace(activeWorkspace) && isWorkspaceSubscriptionRestricted(activeWorkspace)) throw new Error('Subscription is inactive. Chat is read-only until billing is restored.')
      const isHelper = isHelperOnlyAccount({ userProfile, currentUser, workspaces, activeRole }) || activeRole === 'HELPER'
      const participantUids = new Set(members.map((member) => member.uid).filter(Boolean))
      participantUids.add(currentUser.uid)
      if (!isHelper && participantUids.size < 2) throw new Error('Chat unlocks once at least two people (members or helpers) have joined.')

      const trimmed = textContent.trim()
      const attachmentFiles = options?.attachments ?? []
      if (attachmentFiles.length > MAX_CHAT_ATTACHMENTS) throw new Error('Attach up to 5 files per message.')
      if (
        !trimmed
        && attachmentFiles.length === 0
        && (options?.mentionedElementIds?.length ?? 0) === 0
        && (options?.mentionedUserIds?.length ?? 0) === 0
      ) throw new Error('Cannot send an empty message.')
      const quotaUnits = 1 + attachmentFiles.length

      // Quota gate — reject BEFORE any database round-trip.
      if (
        !limitsLiftedForWorkspace(activeWorkspace)
        && workspaceQuota
        && (
          workspaceQuota.isOverQuota
          || (workspaceQuota.limit !== -1 && workspaceQuota.limit > 0 && workspaceQuota.usedToday + quotaUnits > workspaceQuota.limit)
        )
      ) {
        throw new Error(t('NLSChatErrorDailyQuotaReached'))
      }

      const messageRef = doc(
        collection(firestoreDb, 'workspaces', activeWorkspaceId, 'channels', targetChannelId, 'messages'),
      )

      const attachments: ChatAttachment[] = []
      for (const [index, file] of attachmentFiles.entries()) {
        const attachmentId = `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`
        const uploaded = await uploadWorkspaceArtifact({
          workspaceId: activeWorkspaceId,
          file,
          artifactType: 'CHAT_ATTACHMENT',
          artifactId: `${targetChannelId}:${messageRef.id}:${attachmentId}`,
        })
        attachments.push({
          id: uploaded.fileId,
          name: file.name || 'attachment',
          contentType: file.type || 'application/octet-stream',
          size: file.size,
          downloadUrl: uploaded.downloadUrl,
          storagePath: uploaded.storagePath,
        })
      }

      const extractedTags = extractTags(trimmed)
      if (extractedTags.length > 1) throw new Error('Use only one #tag per message.')
      const mentionedUserIds = options?.mentionedUserIds ?? (isHelper ? [] : resolveMentionedUserIds(trimmed, members, currentUser.uid))
      // Merge text-derived metadata first, then let explicitly supplied structured
      // metadata provide its additive extension fields.
      const derivedMetadata: ChatMessageMetadata = {}
      const metadata = { ...derivedMetadata, ...(options?.metadata ?? {}) }
      const messagePayload = {
        id: messageRef.id,
        authorId: currentUser.uid,
        authorName: currentDisplayName || currentUser.email || 'Unknown',
        authorAvatarUrl: currentProfileImageUrl,
        authorRole: toAuthorRole(activeRole),
        textContent: trimmed,
        attachmentCount: attachments.length,
        ...(attachments.length > 0 ? { attachments } : {}),
        timestamp: serverTimestamp(),
        ...(options?.replyToId ? { replyToId: options.replyToId } : {}),
        ...(options?.targetTaskIds && options.targetTaskIds.length > 0
          ? { targetTaskIds: options.targetTaskIds }
          : {}),
        ...(options?.mentionedElementIds && options.mentionedElementIds.length > 0
          ? { mentionedElementIds: Array.from(new Set(options.mentionedElementIds)) }
          : {}),
        ...(mentionedUserIds.length > 0 ? { mentionedUserIds } : {}),
        ...(options?.entityReferences && options.entityReferences.length > 0
          ? { entityReferences: options.entityReferences }
          : {}),
        ...(extractedTags.length > 0 ? { tags: extractedTags } : {}),
        ...(Object.keys(metadata).length > 0 ? { metadata } : {}),
      }

      const channelRef = doc(firestoreDb, 'workspaces', activeWorkspaceId, 'channels', targetChannelId)
      const activeChannel = channelsList.find((channel) => channel.id === targetChannelId)
      if (activeChannel?.archivedAt || activeChannel?.permanentlyDeletedAt) {
        throw new Error('This channel is archived. Restore it before sending new messages.')
      }

      try {
        // Atomic block: write the message, bump the parent tally, and touch
        // the channel's `lastActivity` so the sidebar resorts immediately.
        const batch = writeBatch(firestoreDb)
        batch.set(messageRef, messagePayload)
        chatQuotaWrite(batch, activeWorkspaceId, quotaUnits)
        batch.update(channelRef, { lastActivity: serverTimestamp() })
        await batch.commit()
        fireAutomationEvent(
          {
            type: 'CHAT_MESSAGE_POSTED',
            workspaceId: activeWorkspaceId,
            eventId: `chat-msg-${messageRef.id}`,
            actorId: currentUser.uid,
            payload: {
              channelId: targetChannelId,
              messageId: messageRef.id,
              textPreview: trimmed.slice(0, 120),
              hasMentions: mentionedUserIds.length > 0,
            },
          },
          { id: currentUser.uid },
        )
      } catch (error) {
        console.warn('[Firestore write failed] chat sendMessage', targetChannelId, error)
        throw error instanceof Error ? error : new Error('Failed to send message.')
      }
    },
    [activeChannelId, activeRole, activeWorkspace, activeWorkspaceId, canPerform, channelsList, currentDisplayName, currentProfileImageUrl, currentUser, members, userProfile?.role, workspaceQuota],
  )

  const setHuddleState = useCallback(
    async (state: { isListening: boolean; isSpeaking: boolean }, channelId?: string) => {
      if (!currentUser) throw new Error('You must be signed in to join huddle.')
      if (!canPerform('chat.huddle')) throw new Error('Your workspace role cannot join huddles.')
      if (!activeWorkspaceId) throw new Error('No active workspace selected.')
      const targetChannelId = channelId ?? activeChannelId
      if (!targetChannelId) throw new Error('No active channel selected.')
      setLocalHuddleState(state)
      const presenceNode = rtdbRef(firebaseRtdb, `presence/${activeWorkspaceId}/${targetChannelId}/${currentUser.uid}`)
      const huddleDocRef = doc(firestoreDb, 'workspaces', activeWorkspaceId, 'huddles', targetChannelId)

      if (!state.isListening && !state.isSpeaking) {
        await rtdbRemove(presenceNode).catch(() => undefined)
        // We don't delete the Firestore huddle doc — it's a persistent metadata
        // shell. Participants list is best-effort; readers prune by RTDB cutoff.
        return
      }

      await rtdbSet(presenceNode, {
        uid: currentUser.uid,
        displayName: currentDisplayName || currentUser.email || 'Member',
        photoURL: currentProfileImageUrl,
        isListening: state.isListening,
        isSpeaking: state.isSpeaking,
        updatedAt: rtdbServerTimestamp(),
      })
      rtdbOnDisconnect(presenceNode).remove().catch(() => undefined)

      // Upsert the Firestore huddle metadata shell. We claim host if missing.
      try {
        await runTransaction(firestoreDb, async (tx) => {
          const snap = await tx.get(huddleDocRef)
          const me = {
            uid: currentUser.uid,
            displayName: currentDisplayName || currentUser.email || 'Member',
            avatarUrl: currentProfileImageUrl,
          }
          if (!snap.exists()) {
            tx.set(huddleDocRef, {
              channelId: targetChannelId,
              hostUid: currentUser.uid,
              startedAt: serverTimestamp(),
              participants: [me],
            })
            return
          }
          const data = snap.data() as Record<string, unknown>
          const participants = Array.isArray(data.participants)
            ? (data.participants as Record<string, unknown>[])
            : []
          if (participants.some((p) => p && p.uid === currentUser.uid)) return
          tx.update(huddleDocRef, { participants: [...participants, me] })
        })
      } catch (error) {
        console.warn('[Firestore write failed] huddle metadata', activeWorkspaceId, activeChannelId, error)
      }
    },
    [activeWorkspaceId, activeChannelId, canPerform, currentDisplayName, currentProfileImageUrl, currentUser],
  )

  // ── Typing presence (RTDB, debounced) ────────────────────────────────────
  const setTyping = useCallback(
    async (isTyping: boolean) => {
      if (!activeWorkspaceId || !activeChannelId || !currentUser) return
      const typingNode = rtdbRef(
        firebaseRtdb,
        `typing/${activeWorkspaceId}/${activeChannelId}/${currentUser.uid}`,
      )
      if (typingTimerRef.current) {
        window.clearTimeout(typingTimerRef.current)
        typingTimerRef.current = null
      }
      if (!isTyping) {
        await rtdbRemove(typingNode).catch(() => undefined)
        return
      }
      try {
        await rtdbSet(typingNode, {
          uid: currentUser.uid,
          displayName: currentDisplayName || currentUser.email || 'Member',
          updatedAt: rtdbServerTimestamp(),
        })
        rtdbOnDisconnect(typingNode).remove().catch(() => undefined)
        typingTimerRef.current = window.setTimeout(() => {
          void rtdbRemove(typingNode).catch(() => undefined)
          typingTimerRef.current = null
        }, TYPING_DEBOUNCE_MS)
      } catch (error) {
        console.warn('[RTDB write failed] typing', activeWorkspaceId, activeChannelId, error)
      }
    },
    [activeWorkspaceId, activeChannelId, currentDisplayName, currentUser],
  )

  // ── Mark a channel as read (debounced) ───────────────────────────────────
  const markRead = useCallback(
    async (channelId?: string) => {
      const targetId = channelId ?? activeChannelId
      if (!activeWorkspaceId || !targetId || !currentUser) return
      if (markReadTimerRef.current) window.clearTimeout(markReadTimerRef.current)
      markReadTimerRef.current = window.setTimeout(() => {
        void updateDoc(doc(firestoreDb, 'workspaces', activeWorkspaceId, 'channels', targetId), {
          [`lastReadAt.${currentUser.uid}`]: serverTimestamp(),
        }).catch((error) => {
          console.warn('[Firestore write failed] markRead', activeWorkspaceId, targetId, error)
        })
        markReadTimerRef.current = null
      }, MARK_READ_DEBOUNCE_MS)
    },
    [activeWorkspaceId, activeChannelId, currentUser],
  )

  // ── Channel CRUD ────────────────────────────────────────────────────────
  const createChannel = useCallback(
    async ({ name, type, initialMemberIds, scopeNodeId }: CreateChannelOptions): Promise<string> => {
      if (!currentUser) throw new Error('You must be signed in to create a channel.')
      if (!activeWorkspaceId) throw new Error('No active workspace selected.')
      if (type === 'private' && !canPerform('chat.create_private_channel')) {
        throw new Error('Your workspace role cannot create private channels.')
      }
      if (type === 'public' && !canPerform('chat.create_channel')) {
        throw new Error('Your workspace role cannot create channels.')
      }
      const trimmed = name.trim()
      if (!trimmed) throw new Error('Channel name is required.')
      if (trimmed.length > 64) throw new Error('Channel name is too long (max 64 chars).')
      const slug = slugify(trimmed)
      if (!SLUG_REGEX.test(slug)) throw new Error('Channel name must contain letters or numbers.')

      const channelRef = doc(collection(firestoreDb, 'workspaces', activeWorkspaceId, 'channels'))
      const slugRef = doc(firestoreDb, 'workspaces', activeWorkspaceId, 'channelSlugs', slug)

      await runTransaction(firestoreDb, async (tx) => {
        const slugSnap = await tx.get(slugRef)
        if (slugSnap.exists()) throw new Error(`A channel named "${slug}" already exists.`)
        const roles: Record<string, ChannelRole> = { [currentUser.uid]: 'admin' }
        if (type === 'private') {
          for (const uid of initialMemberIds ?? []) {
            if (uid && uid !== currentUser.uid) roles[uid] = 'member'
          }
        }
        tx.set(channelRef, {
          name: trimmed,
          slug,
          type,
          roles,
          lastActivity: serverTimestamp(),
          lastReadAt: {},
          createdBy: currentUser.uid,
          createdAt: serverTimestamp(),
          ...channelScopeNodeIdField(scopeNodeId),
        })
        tx.set(slugRef, { channelId: channelRef.id, createdBy: currentUser.uid })
      })

      return channelRef.id
    },
    [activeWorkspaceId, canPerform, currentUser],
  )

  const renameChannel = useCallback(
    async (channelId: string, newName: string) => {
      if (!currentUser) throw new Error('You must be signed in.')
      if (!activeWorkspaceId) throw new Error('No active workspace.')
      const trimmed = newName.trim()
      if (!trimmed) throw new Error('Channel name is required.')
      if (trimmed.length > 64) throw new Error('Channel name is too long (max 64 chars).')
      const newSlug = slugify(trimmed)
      if (!SLUG_REGEX.test(newSlug)) throw new Error('Channel name must contain letters or numbers.')

      const channelRef = doc(firestoreDb, 'workspaces', activeWorkspaceId, 'channels', channelId)
      await runTransaction(firestoreDb, async (tx) => {
        const channelSnap = await tx.get(channelRef)
        if (!channelSnap.exists()) throw new Error('Channel not found.')
        const data = channelSnap.data() as Record<string, unknown>
        const oldSlug = typeof data.slug === 'string' ? data.slug : ''
        if (oldSlug === newSlug) {
          tx.update(channelRef, { name: trimmed })
          return
        }
        const newSlugRef = doc(firestoreDb, 'workspaces', activeWorkspaceId, 'channelSlugs', newSlug)
        const newSlugSnap = await tx.get(newSlugRef)
        if (newSlugSnap.exists()) throw new Error(`A channel named "${newSlug}" already exists.`)
        tx.set(newSlugRef, { channelId, createdBy: currentUser.uid })
        tx.update(channelRef, { name: trimmed, slug: newSlug })
        if (oldSlug) {
          const oldSlugRef = doc(firestoreDb, 'workspaces', activeWorkspaceId, 'channelSlugs', oldSlug)
          tx.delete(oldSlugRef)
        }
      })
    },
    [activeWorkspaceId, currentUser],
  )

  const deleteChannel = useCallback(
    async (channelId: string) => {
      if (!currentUser) throw new Error('You must be signed in.')
      if (!activeWorkspaceId) throw new Error('No active workspace.')
      if (channelId === DEFAULT_CHAT_CHANNEL_ID) throw new Error('The default general channel cannot be removed.')

      const channelRef = doc(firestoreDb, 'workspaces', activeWorkspaceId, 'channels', channelId)
      await runTransaction(firestoreDb, async (tx) => {
        const channelSnap = await tx.get(channelRef)
        if (!channelSnap.exists()) throw new Error('Channel not found.')
        const data = channelSnap.data() as Record<string, unknown>
        const roles = data.roles && typeof data.roles === 'object' ? data.roles as Record<string, unknown> : {}
        const isChannelAdmin = roles[currentUser.uid] === 'admin'
        const isWorkspaceManager = roleCanPerform(activeRole, 'EDITOR')
        const isCreator = data.createdBy === currentUser.uid
        if (!isChannelAdmin && !isWorkspaceManager && !isCreator) {
          throw new Error('Only channel admins, creators, or workspace editors can remove this channel.')
        }
        tx.update(channelRef, {
          archivedAt: serverTimestamp(),
          archivedBy: currentUser.uid,
          restoredAt: deleteField(),
          restoredBy: deleteField(),
          lastActivity: serverTimestamp(),
        })
      })

      if (activeChannelId === channelId) {
        setActiveChannelId(channelsList.find((channel) => channel.id !== channelId && !channel.archivedAt && !channel.permanentlyDeletedAt)?.id ?? DEFAULT_CHAT_CHANNEL_ID)
      }
    },
    [activeChannelId, activeRole, activeWorkspaceId, channelsList, currentUser, setActiveChannelId],
  )

  const restoreChannel = useCallback(
    async (channelId: string) => {
      if (!currentUser) throw new Error('You must be signed in.')
      if (!activeWorkspaceId) throw new Error('No active workspace.')
      const channelRef = doc(firestoreDb, 'workspaces', activeWorkspaceId, 'channels', channelId)
      await updateDoc(channelRef, {
        archivedAt: deleteField(),
        archivedBy: deleteField(),
        restoredAt: serverTimestamp(),
        restoredBy: currentUser.uid,
        lastActivity: serverTimestamp(),
      })
    },
    [activeWorkspaceId, currentUser],
  )

  const permanentlyDeleteArchivedChannel = useCallback(
    async (channelId: string) => {
      if (!currentUser) throw new Error('You must be signed in.')
      if (!activeWorkspaceId) throw new Error('No active workspace.')
      if (channelId === DEFAULT_CHAT_CHANNEL_ID) throw new Error('The default general channel cannot be permanently removed.')
      await purgeArchivedChannel({
        workspaceId: activeWorkspaceId,
        channelId,
      })
    },
    [activeWorkspaceId, currentUser],
  )

  const updateChannelMembers = useCallback(
    async (channelId: string, options: UpdateChannelMembersOptions) => {
      if (!activeWorkspaceId) throw new Error('No active workspace.')
      const updates: Record<string, unknown> = { lastActivity: serverTimestamp() }
      for (const uid of options.addMemberIds ?? []) if (uid) updates[`roles.${uid}`] = 'member'
      for (const uid of options.removeMemberIds ?? []) if (uid) updates[`roles.${uid}`] = deleteField()
      for (const uid of options.promoteAdminIds ?? []) if (uid) updates[`roles.${uid}`] = 'admin'
      for (const uid of options.demoteAdminIds ?? []) if (uid) updates[`roles.${uid}`] = 'member'
      await updateDoc(doc(firestoreDb, 'workspaces', activeWorkspaceId, 'channels', channelId), updates)
    },
    [activeWorkspaceId],
  )

  const addMember = useCallback(
    async (channelId: string, uid: string) => {
      if (!activeWorkspaceId) throw new Error('No active workspace.')
      if (!uid) throw new Error('Member uid is required.')
      const channelRef = doc(firestoreDb, 'workspaces', activeWorkspaceId, 'channels', channelId)
      await updateDoc(channelRef, { [`roles.${uid}`]: 'member' })
    },
    [activeWorkspaceId],
  )

  const removeMember = useCallback(
    async (channelId: string, uid: string) => {
      if (!activeWorkspaceId) throw new Error('No active workspace.')
      if (!uid) throw new Error('Member uid is required.')
      const channelRef = doc(firestoreDb, 'workspaces', activeWorkspaceId, 'channels', channelId)
      await updateDoc(channelRef, { [`roles.${uid}`]: deleteField() })
    },
    [activeWorkspaceId],
  )

  const leaveChannel = useCallback(
    async (channelId: string) => {
      if (!currentUser) throw new Error('You must be signed in.')
      if (!activeWorkspaceId) throw new Error('No active workspace.')
      const channel = channelsList.find((c) => c.id === channelId)
      if (channel && channel.type === 'private') {
        const admins = Object.entries(channel.roles).filter(([, role]) => role === 'admin').map(([uid]) => uid)
        if (admins.length === 1 && admins[0] === currentUser.uid) {
          throw new Error('Promote another member to admin before leaving — you are the last admin.')
        }
      }
      const channelRef = doc(firestoreDb, 'workspaces', activeWorkspaceId, 'channels', channelId)
      await updateDoc(channelRef, { [`roles.${currentUser.uid}`]: deleteField() })
    },
    [activeWorkspaceId, channelsList, currentUser],
  )

  const editMessage = useCallback(
    async (messageId: string, textContent: string) => {
      if (!currentUser) throw new Error('You must be signed in.')
      if (!activeWorkspaceId || !activeChannelId) throw new Error('No active chat selected.')
      const trimmed = textContent.trim()
      if (!trimmed) throw new Error('Message cannot be empty.')
      const extractedTags = extractTags(trimmed)
      if (extractedTags.length > 1) throw new Error('Use only one #tag per message.')
      const messageRef = doc(firestoreDb, 'workspaces', activeWorkspaceId, 'channels', activeChannelId, 'messages', messageId)
      await updateDoc(messageRef, {
        textContent: trimmed,
        tags: extractedTags,
        editedAt: serverTimestamp(),
        editedBy: currentUser.uid,
      })
    },
    [activeChannelId, activeWorkspaceId, currentUser],
  )

  const deleteMessage = useCallback(
    async (messageId: string) => {
      if (!currentUser) throw new Error('You must be signed in.')
      if (!activeWorkspaceId || !activeChannelId) throw new Error('No active chat selected.')
      const messageRef = doc(firestoreDb, 'workspaces', activeWorkspaceId, 'channels', activeChannelId, 'messages', messageId)
      await updateDoc(messageRef, {
        isDeleted: true,
        deletedAt: serverTimestamp(),
        deletedBy: currentUser.uid,
      })
    },
    [activeChannelId, activeWorkspaceId, currentUser],
  )

  const restoreMessage = useCallback(
    async (messageId: string) => {
      if (!currentUser) throw new Error('You must be signed in.')
      if (!activeWorkspaceId || !activeChannelId) throw new Error('No active chat selected.')
      const messageRef = doc(firestoreDb, 'workspaces', activeWorkspaceId, 'channels', activeChannelId, 'messages', messageId)
      await updateDoc(messageRef, {
        isDeleted: false,
        deletedAt: deleteField(),
        deletedBy: deleteField(),
        restoredAt: serverTimestamp(),
        restoredBy: currentUser.uid,
      })
    },
    [activeChannelId, activeWorkspaceId, currentUser],
  )

  const unreadByChannel = useMemo<Record<string, boolean>>(() => {
    const out: Record<string, boolean> = {}
    if (!currentUser) return out
    for (const channel of channelsList) {
      const lastActivityMs = timestampMillis(channel.lastActivity)
      if (lastActivityMs === 0) {
        out[channel.id] = false
        continue
      }
      const lastReadMs = timestampMillis(channel.lastReadAt[currentUser.uid])
      out[channel.id] = lastActivityMs > lastReadMs
    }
    return out
  }, [channelsList, currentUser])

  const value = useMemo<ChatContextValue>(
    () => ({
      activeChannelId,
      channelsList,
      messagesStream,
      workspaceQuota,
      members,
      huddlePresence,
      viewerPresence,
      huddleMetadata,
      typingPresence,
      unreadByChannel,
      activeTagFilter,
      isLoading,
      contextPrompt,
      contextMetadata,
      meetingContext,
      setActiveChannelId,
      setActiveTagFilter,
      setContextBoard,
      setMeetingContext,
      chatMode,
      isChatOff,
      pendingCreateChannelDraft,
      requestCreateChannelDraft: setPendingCreateChannelDraft,
      clearCreateChannelDraft: () => setPendingCreateChannelDraft(null),
      sendMessage,
      setHuddleState,
      markRead,
      setTyping,
      createChannel,
      renameChannel,
      deleteChannel,
      restoreChannel,
      permanentlyDeleteArchivedChannel,
      updateChannelMembers,
      addMember,
      removeMember,
      leaveChannel,
      editMessage,
      deleteMessage,
      restoreMessage,
    }),
    [
      activeChannelId, channelsList, contextMetadata, contextPrompt, meetingContext, pendingCreateChannelDraft, messagesStream,
      workspaceQuota, members, huddlePresence, viewerPresence, huddleMetadata, typingPresence, unreadByChannel,
      activeTagFilter, isLoading, sendMessage, setHuddleState, markRead, setTyping,
      createChannel, renameChannel, deleteChannel, restoreChannel, permanentlyDeleteArchivedChannel,
      updateChannelMembers, addMember, removeMember, leaveChannel, editMessage, deleteMessage, restoreMessage,
      chatMode, isChatOff, setActiveChannelId,
    ],
  )

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}
